import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Trash2, FolderOpen, Plus, Minus, X, Edit2, Grid3x3, Download, Database, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { getDataEntry, updateDataEntry, deleteDataEntry } from '../lib/entries';
import { getProject } from '../../projects/lib/projects';
import { useJobImport } from '../../jobs/hooks/useJobImport';
import { useDataEntryRealtime, useDataEntryAutosave } from '../hooks/useDataEntryRealtime';
import type { LocalContentChange } from '../hooks/useDataEntryRealtime';
import { PresenceIndicator } from '../../../shared/components/ui/PresenceIndicator';
import { useAuth } from '../../auth/contexts/AuthContext';
import { useUserProfile } from '../../../shared/hooks/useUserProfile';
import type { DataEntry, Project, TableContent, EditingState } from '../../../shared/lib/supabase';
import type { ImportableJob, MolecularOutputType, ParsingLevel, ParsedMolecularData } from '../../molecules/types/molecular';

interface TableData {
  id: string;
  name: string;
  headers: string[];
  rows: string[][];
  rowNames?: string[];
  notes?: string;
}

// Updated to match PresenceIndicator interface - SIMPLIFIED without avatar_url
interface PresenceUser {
  userId: string;
  userName: string;
  userColor: string;
  online_at: string;
}

// Enhanced Table Editor with Real-time Collaboration - RESPONSIVE COLUMN SIZING
const CollaborativeTableEditor = ({ 
  table, 
  onChange,
  onDelete,
  onExport,
  onStartEditing,
  onLiveContentChange,
  editingStates,
  getLiveContentForCell,
  connectionStatus
}: { 
  table: TableData; 
  onChange: (table: TableData) => void;
  onDelete: () => void;
  onExport: (table: TableData) => void;
  onStartEditing: (tableId: string, cellType: 'header' | 'cell' | 'rowName' | 'tableName' | 'notes', rowIndex?: number, colIndex?: number) => void;
  onLiveContentChange: (change: any) => void;
  editingStates: EditingState[];
  getLiveContentForCell: (tableId: string, cellType: 'header' | 'cell' | 'rowName' | 'tableName' | 'notes', rowIndex?: number, colIndex?: number) => LocalContentChange | undefined;
  connectionStatus: string;
}) => {
  const [editingCell, setEditingCell] = useState<{
    type: 'header' | 'cell' | 'rowName' | 'tableName' | 'notes';
    rowIndex?: number;
    colIndex?: number;
  } | null>(null);
  const [editValue, setEditValue] = useState('');
  const notesRef = useRef<HTMLTextAreaElement>(null);
  const inputRefs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement }>({});
  
  // Simple calculation: columns are always 1/10th of available space
  const hasRowNames = table.rowNames ? true : false;
  const needsScroll = table.headers.length > 10;
  
  // Fixed dimensions
  const rowNameWidth = hasRowNames ? 120 : 0;
  const actionColumnWidth = 60;
  
  // Always use percentage-based calculation for consistent sizing
  // This ensures columns are always 1/10th of available width
  const columnWidth = `calc((100% - ${rowNameWidth}px - ${actionColumnWidth}px) / 10)`;
  
  const columnStyle = {
    width: columnWidth,
    minWidth: columnWidth,
    maxWidth: columnWidth
  };
  
  // Calculate table width for scrolling
  const getTableWidth = () => {
    if (needsScroll) {
      // When scrolling, calculate the actual table width needed
      // Each column is 1/10th of container, so total width scales with column count
      const columnMultiplier = table.headers.length / 10;
      return `calc(${rowNameWidth}px + (100% - ${rowNameWidth}px - ${actionColumnWidth}px) * ${columnMultiplier} + ${actionColumnWidth}px)`;
    }
    return '100%';
  };

  // Debounced function to send live updates
  const debouncedLiveUpdate = useRef<NodeJS.Timeout | null>(null);

  const sendLiveUpdate = useCallback((
    cellType: 'header' | 'cell' | 'rowName' | 'tableName' | 'notes',
    value: string,
    rowIndex?: number,
    colIndex?: number
  ) => {
    if (debouncedLiveUpdate.current) {
      clearTimeout(debouncedLiveUpdate.current);
    }

    debouncedLiveUpdate.current = setTimeout(() => {
      onLiveContentChange({
        tableId: table.id,
        cellType,
        rowIndex,
        colIndex,
        value,
        isTemporary: true
      });
    }, 150); // Send updates every 150ms while typing
  }, [table.id, onLiveContentChange]);

  const clearLiveUpdate = useCallback((
    cellType: 'header' | 'cell' | 'rowName' | 'tableName' | 'notes',
    rowIndex?: number,
    colIndex?: number
  ) => {
    onLiveContentChange({
      tableId: table.id,
      cellType,
      rowIndex,
      colIndex,
      value: '',
      isTemporary: false
    });
  }, [table.id, onLiveContentChange]);

  // Check if a cell is being edited by another user
  const isCellBeingEditedByOther = (cellType: string, rowIndex?: number, colIndex?: number) => {
    return editingStates.some(state => 
      state.tableId === table.id && 
      state.cellType === cellType &&
      state.rowIndex === rowIndex &&
      state.colIndex === colIndex
    );
  };

  // Get the user editing a specific cell
  const getCellEditingUser = (cellType: string, rowIndex?: number, colIndex?: number) => {
    return editingStates.find(state => 
      state.tableId === table.id && 
      state.cellType === cellType &&
      state.rowIndex === rowIndex &&
      state.colIndex === colIndex
    );
  };

  // Get live content or current content for display
  const getDisplayValue = (
    cellType: 'header' | 'cell' | 'rowName' | 'tableName' | 'notes',
    currentValue: string,
    rowIndex?: number,
    colIndex?: number
  ) => {
    // If this is the cell we're currently editing, show our edit value
    if (editingCell?.type === cellType && 
        editingCell?.rowIndex === rowIndex && 
        editingCell?.colIndex === colIndex) {
      return editValue;
    }

    // If someone else is editing this cell, show their live content
    const liveContent = getLiveContentForCell(table.id, cellType, rowIndex, colIndex);
    if (liveContent && liveContent.value) {
      return liveContent.value;
    }

    // Otherwise show current saved content
    return currentValue;
  };

  const startEditing = (type: 'header' | 'cell' | 'rowName' | 'tableName' | 'notes', rowIndex?: number, colIndex?: number) => {
    // Don't start editing if someone else is editing this cell
    if (isCellBeingEditedByOther(type, rowIndex, colIndex)) {
      const editingUser = getCellEditingUser(type, rowIndex, colIndex);
      toast.info(`${editingUser?.userName} is currently editing this ${type}`);
      return;
    }

    let initialValue = '';
    if (type === 'header' && colIndex !== undefined) {
      initialValue = table.headers[colIndex] || '';
    } else if (type === 'cell' && rowIndex !== undefined && colIndex !== undefined) {
      initialValue = table.rows[rowIndex]?.[colIndex] || '';
    } else if (type === 'rowName' && rowIndex !== undefined) {
      initialValue = table.rowNames?.[rowIndex] || '';
    } else if (type === 'tableName') {
      initialValue = table.name;
    }

    setEditingCell({ type, rowIndex, colIndex });
    setEditValue(initialValue);

    // Broadcast editing state
    onStartEditing(table.id, type, rowIndex, colIndex);
  };

  const cancelEditing = () => {
    if (editingCell) {
      clearLiveUpdate(editingCell.type, editingCell.rowIndex, editingCell.colIndex);
    }
    setEditingCell(null);
    setEditValue('');
  };

  const saveCell = () => {
    if (!editingCell) return;

    const { type, rowIndex, colIndex } = editingCell;
    let updatedTable = { ...table };

    if (type === 'header' && colIndex !== undefined) {
      updatedTable.headers = [...table.headers];
      updatedTable.headers[colIndex] = editValue;
    } else if (type === 'cell' && rowIndex !== undefined && colIndex !== undefined) {
      updatedTable.rows = table.rows.map(row => [...row]);
      updatedTable.rows[rowIndex][colIndex] = editValue;
    } else if (type === 'rowName' && rowIndex !== undefined) {
      updatedTable.rowNames = [...(table.rowNames || [])];
      updatedTable.rowNames[rowIndex] = editValue;
    } else if (type === 'tableName') {
      updatedTable.name = editValue;
    }

    // Clear live update and save
    clearLiveUpdate(type, rowIndex, colIndex);
    onChange(updatedTable);
    setEditingCell(null);
    setEditValue('');
  };

  const addColumn = () => {
    const updatedTable = {
      ...table,
      headers: [...table.headers, `Column ${table.headers.length + 1}`],
      rows: table.rows.map(row => [...row, ''])
    };
    onChange(updatedTable);
  };

  const deleteColumn = () => {
    if (table.headers.length <= 1) {
      toast.warning('Cannot delete the last column');
      return;
    }
    const updatedTable = {
      ...table,
      headers: table.headers.slice(0, -1),
      rows: table.rows.map(row => row.slice(0, -1))
    };
    onChange(updatedTable);
  };

  const addRow = () => {
    const newRow = new Array(table.headers.length).fill('');
    const updatedTable = {
      ...table,
      rows: [...table.rows, newRow],
      rowNames: table.rowNames ? [...table.rowNames, `Row ${table.rows.length + 1}`] : undefined
    };
    onChange(updatedTable);
  };

  const deleteRow = () => {
    if (table.rows.length <= 1) {
      toast.warning('Cannot delete the last row');
      return;
    }
    const updatedTable = {
      ...table,
      rows: table.rows.slice(0, -1),
      rowNames: table.rowNames ? table.rowNames.slice(0, -1) : undefined
    };
    onChange(updatedTable);
  };

  const handleInputChange = (value: string) => {
    setEditValue(value);
    if (editingCell) {
      sendLiveUpdate(editingCell.type, value, editingCell.rowIndex, editingCell.colIndex);
    }
  };

  // Auto-resize textarea based on content
  useEffect(() => {
    if (notesRef.current) {
      notesRef.current.style.height = 'auto';
      const scrollHeight = notesRef.current.scrollHeight;
      notesRef.current.style.height = `${scrollHeight}px`;
    }
  }, [table.notes]);

  return (
    <div className="border border-gray-200 rounded-sm p-4 mb-6 relative">
      {/* Connection Status Indicator */}
      {connectionStatus !== 'connected' && (
        <div className="absolute top-2 right-2 flex items-center gap-1 text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded">
          <WifiOff className="w-3 h-3" />
          {connectionStatus}
        </div>
      )}

      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Grid3x3 className="h-5 w-5 text-gray-400" />
          {editingCell?.type === 'tableName' ? (
            <input
              type="text"
              value={editValue}
              onChange={(e) => handleInputChange(e.target.value)}
              className="font-semibold text-lg border border-blue-500 rounded px-2 py-1 max-w-[300px] focus:outline-none focus:ring-2 focus:ring-blue-500"
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveCell();
                }
                if (e.key === 'Escape') cancelEditing();
              }}
              onBlur={saveCell}
              autoFocus
            />
          ) : (
            <h3 
              className={`font-semibold text-lg cursor-pointer hover:text-blue-600 relative`}
              onClick={() => startEditing('tableName')}
            >
              {getDisplayValue('tableName', table.name)}
            </h3>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => onExport(table)}
            className="p-1 text-green-600 hover:bg-green-50 rounded transition-colors"
            title="Export table as CSV"
          >
            <Download className="h-4 w-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-1 text-red-600 hover:bg-red-50 rounded transition-colors"
            title="Delete table"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* TABLE WITH FIXED COLUMN SIZING - 1/10th WIDTH PER COLUMN */}
      <div className="border border-gray-200 rounded-sm bg-white overflow-hidden" style={{ width: '100%' }}>
        {/* Scrollable table area - always constrain to viewport width */}
        <div className={`${needsScroll ? 'overflow-x-auto overflow-y-hidden' : 'w-full'}`} style={{ width: '100%' }}>
          <table className="border-collapse table-fixed"
                 style={{
                   width: getTableWidth(),
                   minWidth: getTableWidth()
                 }}>
            <thead>
              <tr>
                {table.rowNames && (
                  <th className="border border-gray-300 bg-gray-50 w-[120px] p-0">
                    <div className="h-[40px]"></div>
                  </th>
                )}
                {table.headers.map((header, col) => {
                  const isEditing = editingCell?.type === 'header' && editingCell?.colIndex === col;
                  
                  return (
                    <th 
                      key={col} 
                      className={`${isEditing ? 'border-2 border-blue-500 bg-gray-50' : 'border border-gray-300 bg-gray-50'} p-0`}
                      style={{
                        width: columnStyle.width,
                        minWidth: columnStyle.minWidth,
                        maxWidth: columnStyle.maxWidth
                      }}>
                      <div className="h-[40px] relative">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => handleInputChange(e.target.value)}
                            className="absolute inset-0 w-full h-full px-2 py-1 text-sm font-medium bg-transparent border-none focus:outline-none resize-none"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                saveCell();
                              }
                              if (e.key === 'Escape') cancelEditing();
                            }}
                            onBlur={saveCell}
                            autoFocus
                          />
                        ) : (
                          <div
                            className="absolute inset-0 w-full h-full px-2 py-1 cursor-pointer font-medium hover:bg-blue-50 flex items-center overflow-hidden"
                            onClick={() => startEditing('header', undefined, col)}
                            title={getDisplayValue('header', header, undefined, col)}
                          >
                            <span className="truncate">
                              {getDisplayValue('header', header, undefined, col)}
                            </span>
                          </div>
                        )}
                      </div>
                    </th>
                  );
                })}
                {/* Add/Delete column button header */}
                <th className="border border-gray-300 w-[60px] p-0" style={{ backgroundColor: '#f9fafb' }}>
                  <div className="h-[40px] flex items-center justify-center" style={{ backgroundColor: '#f9fafb' }}>
                    <button
                      onClick={addColumn}
                      className="flex-1 h-full flex items-center justify-center hover:bg-blue-100 transition-colors"
                      title="Add column"
                    >
                      <Plus className="h-4 w-4 text-gray-600" />
                    </button>
                    <div className="w-px h-6 bg-gray-300"></div>
                    <button
                      onClick={deleteColumn}
                      className="flex-1 h-full flex items-center justify-center hover:bg-red-100 transition-colors"
                      title="Delete last column"
                    >
                      <Minus className="h-4 w-4 text-gray-600" />
                    </button>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, rowIndex) => (
                <tr key={rowIndex}>
                  {table.rowNames && (
                    <td className={`${editingCell?.type === 'rowName' && editingCell?.rowIndex === rowIndex ? 'border-2 border-blue-500 bg-gray-50' : 'border border-gray-300 bg-gray-50'} font-medium w-[120px] p-0`}>
                      <div className="h-[40px] relative">
                        {editingCell?.type === 'rowName' && editingCell?.rowIndex === rowIndex ? (
                          <input
                            type="text"
                            value={editValue}
                            onChange={(e) => handleInputChange(e.target.value)}
                            className="absolute inset-0 w-full h-full px-2 py-1 text-sm font-medium bg-transparent border-none focus:outline-none resize-none"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                saveCell();
                              }
                              if (e.key === 'Escape') cancelEditing();
                            }}
                            onBlur={saveCell}
                            autoFocus
                          />
                        ) : (
                          <div
                            className="absolute inset-0 w-full h-full px-2 py-1 cursor-pointer font-medium hover:bg-blue-50 flex items-center overflow-hidden"
                            onClick={() => startEditing('rowName', rowIndex)}
                            title={getDisplayValue('rowName', table.rowNames?.[rowIndex] || `Row ${rowIndex + 1}`, rowIndex)}
                          >
                            <span className="truncate">
                              {getDisplayValue('rowName', table.rowNames?.[rowIndex] || `Row ${rowIndex + 1}`, rowIndex)}
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                  )}
                  {row.map((cell, colIndex) => {
                    const isEditing = editingCell?.type === 'cell' && 
                                     editingCell?.rowIndex === rowIndex && editingCell?.colIndex === colIndex;
                    
                    return (
                      <td 
                        key={colIndex} 
                        className={`border border-gray-300 p-0 ${isEditing ? 'bg-blue-100' : ''}`}
                        style={{
                          width: columnStyle.width,
                          minWidth: columnStyle.minWidth,
                          maxWidth: columnStyle.maxWidth
                        }}>
                        <div className="h-[40px] relative">
                          {isEditing ? (
                            <input
                              type="text"
                              value={editValue}
                              onChange={(e) => handleInputChange(e.target.value)}
                              className="absolute inset-0 w-full h-full px-2 py-1 text-sm bg-transparent border-none focus:outline-none resize-none"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  e.preventDefault();
                                  saveCell();
                                }
                                if (e.key === 'Escape') cancelEditing();
                              }}
                              onBlur={saveCell}
                              autoFocus
                            />
                          ) : (
                            <div
                              className="absolute inset-0 w-full h-full px-2 py-1 cursor-pointer hover:bg-blue-50 flex items-center overflow-hidden"
                              onClick={() => startEditing('cell', rowIndex, colIndex)}
                              title={getDisplayValue('cell', cell, rowIndex, colIndex)}
                            >
                              <span className="truncate">
                                {getDisplayValue('cell', cell, rowIndex, colIndex)}
                              </span>
                            </div>
                          )}
                        </div>
                      </td>
                    );
                  })}
                  {/* Gray cell for column controls column - merge all cells below header */}
                  {rowIndex === 0 ? (
                    <td rowSpan={table.rows.length} className="border border-gray-300 w-[60px] p-0" style={{ backgroundColor: '#f9fafb' }}>
                      <div className="h-full" style={{ backgroundColor: '#f9fafb' }}></div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {/* Add/Delete row controls - outside scrollable area */}
        <div className="border-t border-gray-300 bg-gray-50">
          <div className="flex">
            {table.rowNames && (
              <div className="w-[120px] border-r border-gray-300 p-0">
                <div className="h-[40px] flex items-center justify-center">
                  <button
                    onClick={addRow}
                    className="flex-1 h-full flex items-center justify-center hover:bg-blue-100 transition-colors"
                    title="Add row"
                  >
                    <Plus className="h-4 w-4 text-gray-600" />
                  </button>
                  <div className="w-px h-6 bg-gray-300"></div>
                  <button
                    onClick={deleteRow}
                    className="flex-1 h-full flex items-center justify-center hover:bg-red-100 transition-colors"
                    title="Delete last row"
                  >
                    <Minus className="h-4 w-4 text-gray-600" />
                  </button>
                </div>
              </div>
            )}
            <div className="flex-1 flex items-center justify-start px-4">
              {!table.rowNames && (
                <div className="flex items-center">
                  <button
                    onClick={addRow}
                    className="px-3 py-1.5 flex items-center gap-1 hover:bg-blue-100 rounded transition-colors"
                    title="Add row"
                  >
                    <Plus className="h-4 w-4 text-gray-600" />
                    <span className="text-sm text-gray-600">Add row</span>
                  </button>
                  <div className="w-px h-6 bg-gray-300 mx-2"></div>
                  <button
                    onClick={deleteRow}
                    className="px-3 py-1.5 flex items-center gap-1 hover:bg-red-100 rounded transition-colors"
                    title="Delete last row"
                  >
                    <Minus className="h-4 w-4 text-gray-600" />
                    <span className="text-sm text-gray-600">Delete row</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">Table Notes</label>
        <textarea
          ref={notesRef}
          value={table.notes || ''}
          onChange={(e) => {
            const updatedTable = { ...table, notes: e.target.value };
            onChange(updatedTable);
          }}
          placeholder="Add notes about this table..."
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          style={{ minHeight: '3rem' }}
        />
      </div>
    </div>
  );
};

interface DataEntryDetailPageProps {
  dataEntryId?: string;
}

export function DataEntryDetailPage({ dataEntryId: propDataEntryId }: DataEntryDetailPageProps = {}) {
  const { entryId: paramEntryId } = useParams<{ entryId: string }>();
  const entryId = propDataEntryId || paramEntryId;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const [entry, setEntry] = useState<DataEntry | null>(null);
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);
  const [entryName, setEntryName] = useState('');
  const [content, setContent] = useState<TableContent>({
    type: 'data_table',
    notes: '',
    tables: [],
    metadata: { table_count: 0, version: 1 }
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showTableDimensionDialog, setShowTableDimensionDialog] = useState(false);
  const [newTableRows, setNewTableRows] = useState(3);
  const [newTableCols, setNewTableCols] = useState(3);
  const [showJobImportDialog, setShowJobImportDialog] = useState(false);
  const entryNotesRef = useRef<HTMLTextAreaElement>(null);

  // Enhanced presence state - UPDATED to match PresenceIndicator interface
  const [presenceUsers, setPresenceUsers] = useState<PresenceUser[]>([]);
  const [userColor] = useState(() => {
    const colors = ['#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4', '#FECA57', '#FF9FF3', '#A8E6CF', '#FFD93D'];
    return colors[Math.floor(Math.random() * colors.length)];
  });

  // Job import hook
  const {
    jobs,
    selectedJob,
    parsedData,
    previewData,
    loading: jobsLoading,
    parsing,
    error: importError,
    progress,
    loadJobs,
    selectJob,
    parseJobOutput,
    clearParsedData,
    clearError
  } = useJobImport({ autoLoadJobs: false });

  // Handle remote content updates
  const handleRemoteContentUpdate = useCallback((newContent: TableContent, userId: string) => {
    setContent(newContent);
    // Removed toast notification
  }, []);

  // Handle live content changes
  const handleLiveContentChange = useCallback((change: LocalContentChange) => {
    // The component will handle displaying live changes via getLiveContentForCell
  }, []);

  // Real-time collaboration with enhanced presence
  const {
    connectionStatus,
    presenceUsers: realtimePresenceUsers,
    editingStates,
    liveContentChanges,
    userColor: realtimeUserColor,
    broadcastContentChange,
    broadcastLiveContentChange,
    broadcastEditingState,
    getLiveContentForCell,
    setCurrentContent
  } = useDataEntryRealtime({
    entryId: entryId || '',
    projectId: entry?.project_id,
    onContentUpdate: handleRemoteContentUpdate,
    onConnectionChange: (status) => {
      // Connection status changes handled silently
    },
    onLiveContentChange: handleLiveContentChange
  });

  // Debug what useDataEntryRealtime is actually returning
  console.log('🔍 Raw presence data:', {
    realtimePresenceUsersLength: realtimePresenceUsers?.length,
    realtimePresenceUsers: realtimePresenceUsers,
    connectionStatus,
    currentUserId: user?.id
  });

  // Log each user individually to see what's in the array
  realtimePresenceUsers.forEach((u, index) => {
    console.log(`🔍 User ${index}:`, {
      userId: u.userId,
      userName: u.userName,
      isCurrentUser: u.userId === user?.id
    });
  });

  // Enhanced presence management - CLEANED UP with aggressive current user filtering
  useEffect(() => {
    if (!user || !profile) return;

    // Create current user
    const currentUser: PresenceUser = {
      userId: user.id,
      userName: profile.first_name && profile.last_name 
        ? `${profile.first_name} ${profile.last_name}` 
        : user.email || 'Unknown User',
      userColor: userColor,
      online_at: new Date().toISOString()
    };

    // AGGRESSIVELY filter out current user - check multiple possible ID fields
    const formattedPresenceUsers: PresenceUser[] = realtimePresenceUsers
      .filter(u => {
        // Check multiple possible ways the current user might be identified
        const isCurrentUser = u.userId === user.id || 
                             (u as any).id === user.id || 
                             (u as any).user_id === user.id;
        return !isCurrentUser;
      })
      .map(u => ({
        userId: u.userId,
        userName: u.userName,
        userColor: u.userColor,
        online_at: u.online_at
      }));

    console.log('🔍 Final presence calculation:', {
      currentUserId: user.id,
      rawCount: realtimePresenceUsers.length,
      filteredCount: formattedPresenceUsers.length,
      shouldShow: formattedPresenceUsers.length === 0 ? 'Working alone' : `${formattedPresenceUsers.length} other collaborator${formattedPresenceUsers.length !== 1 ? 's' : ''} online`
    });

    // Set presence users with current user first, then add other users
    setPresenceUsers([currentUser, ...formattedPresenceUsers]);
  }, [realtimePresenceUsers, user, profile, userColor]);

  // Update current content whenever content changes
  useEffect(() => {
    if (content) {
      setCurrentContent(content);
    }
  }, [content, setCurrentContent]);

  // Auto-save functionality
  const saveToDatabase = useCallback(async (contentToSave: TableContent) => {
    if (!entry || !entryName) return;

    setIsSaving(true);
    try {
      await updateDataEntry(entry.id, {
        name: entryName,
        content: contentToSave
      });
      setLastSaved(new Date());
    } catch (error) {
      console.error('❌ Failed to save data entry:', error);
      throw error;
    } finally {
      setIsSaving(false);
    }
  }, [entry, entryName]);

  useDataEntryAutosave(content, saveToDatabase, 1000);

  useEffect(() => {
    if (entryId) {
      loadEntry();
    }
  }, [entryId]);

  // Auto-resize entry notes textarea
  const resizeTextarea = () => {
    if (entryNotesRef.current) {
      entryNotesRef.current.style.height = 'auto';
      const scrollHeight = entryNotesRef.current.scrollHeight;
      entryNotesRef.current.style.height = `${scrollHeight}px`;
    }
  };

  useEffect(() => {
    resizeTextarea();
  }, [content.notes]);

  useEffect(() => {
    if (!loading && content.notes) {
      setTimeout(resizeTextarea, 10);
    }
  }, [loading, content.notes]);

  const loadEntry = async () => {
    try {
      setLoading(true);
      const entryData = await getDataEntry(entryId!);
      
      if (!entryData) {
        toast.error('Data entry not found');
        navigate('/data-entries');
        return;
      }

      setEntry(entryData);
      setEntryName(entryData.name);
      
      if (entryData.content) {
        setContent(entryData.content);
      }

      // Load project
      const projectData = await getProject(entryData.project_id);
      if (projectData) {
        setProject(projectData);
      }
    } catch (error) {
      console.error('Error loading data entry:', error);
      toast.error('Failed to load data entry');
      navigate('/data-entries');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!entry) return;

    try {
      await deleteDataEntry(entry.id);
      toast.success('Data entry deleted successfully');
      navigate('/data-entries');
    } catch (error) {
      console.error('Error deleting data entry:', error);
      toast.error('Failed to delete data entry');
    }
  };

  const handleTableChange = (index: number, updatedTable: TableData) => {
    const newContent = {
      ...content,
      tables: content.tables.map((table, i) => i === index ? updatedTable : table),
      metadata: {
        ...content.metadata,
        version: content.metadata.version + 1
      }
    };
    setContent(newContent);
    
    // Broadcast changes if connected
    if (connectionStatus === 'connected') {
      broadcastContentChange(newContent, 'table_update', updatedTable.id, updatedTable);
    }
  };

  const handleTableDelete = (index: number) => {
    const tableToDelete = content.tables[index];
    const newContent = {
      ...content,
      tables: content.tables.filter((_, i) => i !== index),
      metadata: {
        table_count: content.tables.length - 1,
        version: content.metadata.version + 1
      }
    };
    setContent(newContent);
    
    // Broadcast changes if connected
    if (connectionStatus === 'connected') {
      broadcastContentChange(newContent, 'table_delete', tableToDelete.id);
    }
  };

  const addNewTable = (rows: number = 3, cols: number = 3) => {
    const headers = Array.from({ length: cols }, (_, i) => `Column ${i + 1}`);
    const tableRows = Array.from({ length: rows }, () => Array(cols).fill(''));
    const rowNames = Array.from({ length: rows }, (_, i) => `Row ${i + 1}`);
    
    const newTable: TableData = {
      id: `table-${Date.now()}`,
      name: `Table ${content.tables.length + 1}`,
      headers,
      rows: tableRows,
      rowNames
    };

    const newContent = {
      ...content,
      tables: [...content.tables, newTable],
      metadata: {
        table_count: content.tables.length + 1,
        version: content.metadata.version + 1
      }
    };
    setContent(newContent);
    setShowTableDimensionDialog(false);
    
    // Broadcast changes if connected
    if (connectionStatus === 'connected') {
      broadcastContentChange(newContent, 'table_add', newTable.id, newTable);
    }
  };

  const handleNotesChange = (notes: string) => {
    const newContent = {
      ...content,
      notes,
      metadata: {
        ...content.metadata,
        version: content.metadata.version + 1
      }
    };
    setContent(newContent);
    
    // Broadcast changes if connected
    if (connectionStatus === 'connected') {
      broadcastContentChange(newContent, 'notes_update');
    }
  };

  const exportTableAsCSV = (table: TableData) => {
    let csvContent = '';
    
    if (table.rowNames) {
      csvContent += ',' + table.headers.join(',') + '\n';
    } else {
      csvContent += table.headers.join(',') + '\n';
    }
    
    table.rows.forEach((row, index) => {
      let rowContent = '';
      if (table.rowNames && table.rowNames[index]) {
        rowContent += table.rowNames[index] + ',';
      }
      rowContent += row.join(',');
      csvContent += rowContent + '\n';
    });
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      const fileName = `${table.name.replace(/[^a-z0-9]/gi, '_').toLowerCase()}.csv`;
      link.setAttribute('href', url);
      link.setAttribute('download', fileName);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success(`Table exported as ${fileName}`);
    }
  };

  const convertParsedDataToTable = (parsedData: ParsedMolecularData, job: ImportableJob, outputType: MolecularOutputType): TableData => {
    const outputTypeNames = {
      proteinDesign: 'Protein Design',
      activeSite: 'Active Site',
      neighbors: 'Neighbors'
    };
    
    const tableName = `${job.name} - ${outputTypeNames[outputType]}`;
    
    let rowNames: string[] | undefined;
    if (parsedData.type === 'residue' && parsedData.rows.length > 0) {
      rowNames = parsedData.rows.map((row, index) => `${parsedData.type === 'residue' ? 'Res' : 'Row'} ${index + 1}`);
    }
    
    return {
      id: `table-${Date.now()}`,
      name: tableName,
      headers: parsedData.headers,
      rows: parsedData.rows,
      rowNames,
      notes: `Imported from job: ${job.name}\nOutput type: ${outputTypeNames[outputType]}\nParsing level: ${parsedData.type}\nFile size: ${parsedData.metadata.fileSize ? Math.round(parsedData.metadata.fileSize / 1024) + ' KB' : 'Unknown'}\nAtoms: ${parsedData.metadata.atomCount || 'Unknown'}\nResidues: ${parsedData.metadata.residueCount || 'Unknown'}`
    };
  };

  const handleImportFromJob = (parsedData: ParsedMolecularData, job: ImportableJob, outputType: MolecularOutputType) => {
    const newTable = convertParsedDataToTable(parsedData, job, outputType);
    const newContent = {
      ...content,
      tables: [...content.tables, newTable],
      metadata: {
        table_count: content.tables.length + 1,
        version: content.metadata.version + 1
      }
    };
    setContent(newContent);
    setShowJobImportDialog(false);
    clearParsedData();
    toast.success(`Table imported from job: ${job.name}`);
    
    // Broadcast changes if connected
    if (connectionStatus === 'connected') {
      broadcastContentChange(newContent, 'table_add', newTable.id, newTable);
    }
  };

  const getConnectionStatusIcon = () => {
    switch (connectionStatus) {
      case 'connected': return <Wifi className="w-4 h-4 text-green-500" />;
      case 'reconnecting': return <Wifi className="w-4 h-4 text-yellow-500 animate-pulse" />;
      case 'disconnected': return <WifiOff className="w-4 h-4 text-red-500" />;
      default: return <WifiOff className="w-4 h-4 text-gray-500" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center bg-white w-full h-full min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-400"></div>
      </div>
    );
  }

  if (!entry) {
    return (
      <div className="text-center py-12 bg-white w-full h-full min-h-screen">
        <p className="text-gray-600">Data entry not found</p>
        <Link to="/data-entries" className="text-blue-600 hover:text-blue-700 mt-4 inline-block">
          Back to Data Entries
        </Link>
      </div>
    );
  }

  return (
    <div className="w-full h-full p-6 bg-white overflow-auto">
      {/* Enhanced Collaboration Header - UPDATED to match NotebookEditor */}
      <div className="bg-gradient-to-r from-white to-gray-50 border-b border-gray-200 p-4 shadow-sm mb-6 rounded-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-6">
            <PresenceIndicator 
              users={presenceUsers} 
              currentUserId={user?.id || ''} 
              className="text-sm"
            />
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-3">
              {getConnectionStatusIcon()}
              <span className={`text-sm font-medium ${
                connectionStatus === 'connected' ? 'text-green-600' : 
                connectionStatus === 'reconnecting' ? 'text-yellow-600' : 
                'text-red-600'
              }`}>
                {connectionStatus === 'connected' ? 'Connected' : 
                 connectionStatus === 'reconnecting' ? 'Reconnecting' : 
                 'Disconnected'}
              </span>
            </div>
            
            <div className="flex items-center gap-3">
              {isSaving ? (
                <>
                  <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                  <span className="text-sm text-blue-600 font-medium">Saving...</span>
                </>
              ) : lastSaved ? (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-600">
                    Auto-saved {lastSaved.toLocaleTimeString()}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-600">Auto-saving enabled</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <input
              type="text"
              value={entryName}
              onChange={(e) => setEntryName(e.target.value)}
              className="text-3xl font-bold text-gray-900 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-blue-500 rounded px-2 -ml-2"
            />
            {project && (
              <div className="flex items-center gap-2 mt-1">
                <FolderOpen className="h-4 w-4 text-gray-400" />
                <Link 
                  to={`/projects/${project.id}`}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  {project.name}
                </Link>
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowDeleteDialog(true)}
              className="p-2 text-red-600 hover:bg-red-50 rounded-sm transition-colors"
            >
              <Trash2 className="h-5 w-5" />
            </button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="bg-white rounded-sm border border-gray-200 p-6 relative">
        {/* Editing Indicators - Top level */}
        {editingStates.length > 0 && (
          <div className="absolute top-4 right-4 flex flex-col gap-1 z-20">
            {editingStates.map((state, index) => (
              <div
                key={`${state.userId}-${state.cellType}-${state.rowIndex}-${state.colIndex}-${index}`}
                className="bg-white border-2 rounded-sm px-3 py-1 shadow-md text-xs"
                style={{ borderColor: state.userColor }}
              >
                <span style={{ color: state.userColor }} className="font-medium">{state.userName}</span> is editing {state.cellType}
              </div>
            ))}
          </div>
        )}

        {/* Notes */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">Entry Notes</label>
          <textarea
            ref={entryNotesRef}
            value={content.notes}
            onChange={(e) => handleNotesChange(e.target.value)}
            placeholder="Add notes about this data entry..."
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm resize-none overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            style={{ minHeight: '3rem', maxHeight: '20rem' }}
          />
        </div>

        {/* Tables */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Data Tables</h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowTableDimensionDialog(true)}
                className="flex items-center gap-2 px-3 py-1.5 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
              >
                <Plus className="h-4 w-4" />
                Add Table
              </button>
              <button
                onClick={() => {
                  setShowJobImportDialog(true);
                  if (project?.id) {
                    loadJobs(project.id);
                  }
                }}
                className="flex items-center gap-2 px-3 py-1.5 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors text-sm"
              >
                <Database className="h-4 w-4" />
                Import from Job
              </button>
            </div>
          </div>

          {content.tables.length === 0 ? (
            <div className="text-center py-8 bg-gray-50 rounded-sm">
              <Grid3x3 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-600 mb-4">No tables yet</p>
              <button
                onClick={() => setShowTableDimensionDialog(true)}
                className="text-blue-600 hover:text-blue-700"
              >
                Add your first table
              </button>
            </div>
          ) : (
            content.tables.map((table, index) => (
              <CollaborativeTableEditor
                key={table.id}
                table={table}
                onChange={(updatedTable) => handleTableChange(index, updatedTable)}
                onDelete={() => handleTableDelete(index)}
                onExport={exportTableAsCSV}
                onStartEditing={(tableId: string, cellType: 'header' | 'cell' | 'rowName' | 'tableName' | 'notes', rowIndex?: number, colIndex?: number) => {
                  broadcastEditingState({
                    tableId,
                    cellType,
                    rowIndex,
                    colIndex
                  });
                }}
                onLiveContentChange={broadcastLiveContentChange}
                editingStates={editingStates}
                getLiveContentForCell={getLiveContentForCell}
                connectionStatus={connectionStatus}
              />
            ))
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4 text-red-600">Delete Data Entry</h2>
            <p className="text-gray-700 mb-6">
              Are you sure you want to delete "<strong>{entry.name}</strong>"? 
              This action cannot be undone and all tables will be permanently removed.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteDialog(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
              >
                Delete Data Entry
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table Dimension Dialog */}
      {showTableDimensionDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Create New Table</h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Rows</label>
                <input
                  type="number"
                  value={newTableRows}
                  onChange={(e) => setNewTableRows(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1" max="50"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Number of Columns</label>
                <input
                  type="number"
                  value={newTableCols}
                  onChange={(e) => setNewTableCols(Math.max(1, parseInt(e.target.value) || 1))}
                  min="1" max="20"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-6">
              <button
                onClick={() => {
                  setShowTableDimensionDialog(false);
                  setNewTableRows(3);
                  setNewTableCols(3);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => addNewTable(newTableRows, newTableCols)}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
              >
                Create Table
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Import Dialog */}
      {showJobImportDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-sm p-6 w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-semibold mb-4">Import Table from Job Results</h2>
            
            {/* Job Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Job
              </label>
              {jobsLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-2 text-gray-600">Loading jobs...</span>
                </div>
              ) : jobs.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Database className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>No completed jobs found in this project</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 max-h-40 overflow-y-auto">
                  {jobs.map((job) => (
                    <button
                      key={job.id}
                      onClick={() => selectJob(job)}
                      className={`text-left p-3 rounded-sm border transition-colors ${
                        selectedJob?.id === job.id
                          ? 'bg-blue-50 border-blue-500'
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <div className="font-medium text-sm">{job.name}</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {new Date(job.created_at).toLocaleDateString()}
                      </div>
                      <div className="flex gap-1 mt-2">
                        {job.availableOutputs.proteinDesign && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">Protein</span>
                        )}
                        {job.availableOutputs.activeSite && (
                          <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded">Active Site</span>
                        )}
                        {job.availableOutputs.neighbors && (
                          <span className="px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded">Neighbors</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Output Type Selection */}
            {selectedJob && (
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Output Type
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {selectedJob.availableOutputs.proteinDesign && (
                    <button
                      onClick={() => parseJobOutput('proteinDesign', 'residue')}
                      disabled={parsing}
                      className="p-4 border rounded-sm hover:bg-blue-50 hover:border-blue-300 transition-colors disabled:opacity-50"
                    >
                      <div className="text-sm font-medium text-blue-700">Protein Design</div>
                      <div className="text-xs text-gray-500 mt-1">Residue-level analysis</div>
                    </button>
                  )}
                  {selectedJob.availableOutputs.activeSite && (
                    <button
                      onClick={() => parseJobOutput('activeSite', 'binding')}
                      disabled={parsing}
                      className="p-4 border rounded-sm hover:bg-green-50 hover:border-green-300 transition-colors disabled:opacity-50"
                    >
                      <div className="text-sm font-medium text-green-700">Active Site</div>
                      <div className="text-xs text-gray-500 mt-1">Binding interactions</div>
                    </button>
                  )}
                  {selectedJob.availableOutputs.neighbors && (
                    <button
                      onClick={() => parseJobOutput('neighbors', 'chain')}
                      disabled={parsing}
                      className="p-4 border rounded-sm hover:bg-amber-50 hover:border-amber-300 transition-colors disabled:opacity-50"
                    >
                      <div className="text-sm font-medium text-amber-700">Neighbors</div>
                      <div className="text-xs text-gray-500 mt-1">Chain-level overview</div>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Parsing Progress */}
            {parsing && progress && (
              <div className="mb-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Processing...</span>
                  <span className="text-sm text-gray-500">{progress.progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-sm h-2">
                  <div 
                    className="bg-blue-600 h-2 rounded-sm transition-all duration-300"
                    style={{ width: `${progress.progress}%` }}
                  />
                </div>
                {progress.currentItem && (
                  <p className="text-xs text-gray-500 mt-1">{progress.currentItem}</p>
                )}
              </div>
            )}

            {/* Error Display */}
            {importError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-sm">
                <div className="flex items-center gap-2">
                  <X className="h-5 w-5 text-red-500" />
                  <span className="font-medium text-red-700">Import Error</span>
                </div>
                <p className="text-sm text-red-600 mt-1">{importError.message}</p>
                {importError.recoverable && (
                  <button
                    onClick={clearError}
                    className="mt-2 text-sm text-red-600 hover:text-red-800"
                  >
                    Try Again
                  </button>
                )}
              </div>
            )}

            {/* Preview Table */}
            {previewData && (
              <div className="mb-6">
                <h3 className="text-lg font-medium mb-3">Preview (first 10 rows)</h3>
                <div className="overflow-x-auto border border-gray-200 rounded-sm max-w-full">
                  <table className="border-collapse">
                    <thead>
                      <tr>
                        {previewData.headers.map((header, index) => (
                          <th key={index} className="border border-gray-300 p-2 bg-gray-50 text-left text-sm font-medium">
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {previewData.rows.map((row, rowIndex) => (
                        <tr key={rowIndex}>
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex} className="border border-gray-300 p-2 text-sm">
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-2 text-xs text-gray-500">
                  Total rows: {parsedData?.rows.length || 0} | Columns: {previewData.headers.length}
                </div>
              </div>
            )}

            {/* Dialog Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowJobImportDialog(false);
                  clearParsedData();
                  clearError();
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              {parsedData && selectedJob && (
                <button
                  onClick={() => {
                    const outputType = selectedJob.availableOutputs.proteinDesign && parsedData.type === 'residue' ? 'proteinDesign' :
                                     selectedJob.availableOutputs.activeSite && parsedData.type === 'binding' ? 'activeSite' :
                                     'neighbors';
                    handleImportFromJob(parsedData, selectedJob, outputType);
                  }}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                >
                  Import Table
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}